import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { Themes, ThemesType } from '@/types/theme';

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: ThemesType;
  storageKey?: string;
};

type ThemeProviderState = {
  theme: ThemesType;
  setTheme: (theme: ThemesType) => void;
};

const initialState: ThemeProviderState = {
  theme: Themes.SYSTEM,
  setTheme: () => null,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

export function ThemeProvider({
  children,
  defaultTheme = Themes.SYSTEM,
  storageKey = 'vite-ui-theme',
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<ThemesType>(
    () => (localStorage.getItem(storageKey) as ThemesType) || defaultTheme
  );

  useEffect(() => {
    const root = window.document.documentElement;

    root.classList.remove(Themes.LIGHT, Themes.DARK);

    if (theme === Themes.SYSTEM) {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
        ? Themes.DARK
        : Themes.LIGHT;

      root.classList.add(systemTheme);
      return;
    }

    root.classList.add(theme);
  }, [theme]);

  const value = {
    theme,
    setTheme: (theme: ThemesType) => {
      localStorage.setItem(storageKey, theme);
      setTheme(theme);
    },
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

/**
 * Hook for accessing theme state and functionality.
 *
 * @returns An object containing:
 *   - theme: The user's theme setting (can be SYSTEM, DARK, or LIGHT)
 *   - resolvedTheme: The actual theme being displayed (DARK or LIGHT, never SYSTEM)
 *   - setTheme: Function to update the theme setting
 *
 * @example
 * ```tsx
 * const { theme, resolvedTheme, setTheme } = useTheme();
 *
 * // Show user's setting in UI
 * <span>{theme}</span> // "system", "dark", or "light"
 *
 * // Use actual theme for styling
 * <img src={resolvedTheme === Themes.LIGHT ? darkLogo : lightLogo} />
 *
 * // Change theme
 * <button onClick={() => setTheme(Themes.DARK)}>Dark Mode</button>
 * ```
 */
export const useTheme = () => {
  const context = useContext(ThemeProviderContext);

  if (context === undefined) throw new Error('useTheme must be used within a ThemeProvider');

  const resolvedTheme = useMemo((): Themes.DARK | Themes.LIGHT => {
    if (context.theme === Themes.DARK) {
      return Themes.DARK;
    }

    if (context.theme === Themes.SYSTEM) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? Themes.DARK : Themes.LIGHT;
    }

    return Themes.LIGHT;
  }, [context.theme]);

  return {
    theme: context.theme,
    resolvedTheme,
    setTheme: context.setTheme,
  };
};
